/**
 * Ortssuche über Nominatim (OSM). Für Produktion mit eigener Instanz oder
 * einem Geocoder mit Nutzungsvertrag ersetzen – siehe README.
 */
import { kategorieFinden, classLabel } from '../map/poiCategories.js';

const ENDPOINT = 'https://nominatim.openstreetmap.org/search';

/** Unterhalb davon führen die Kacheln keine Punkte von Interesse mehr. */
const POI_AB_ZOOM = 13;

/** Einzelnes Suchergebnis zu einem freien Text – fuer die Routenfelder. */
export async function geocode(query, map) {
  const b = map.getBounds();
  const params = new URLSearchParams({
    q: query,
    format: 'jsonv2',
    limit: '1',
    'accept-language': 'de-CH',
    viewbox: [b.getWest(), b.getNorth(), b.getEast(), b.getSouth()].join(','),
    bounded: '0'
  });
  const res = await fetch(`${ENDPOINT}?${params}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Ortssuche nicht erreichbar.');
  const [hit] = await res.json();
  if (!hit) throw new Error(`"${query}" nicht gefunden.`);
  return { lat: Number(hit.lat), lng: Number(hit.lon), name: hit.name || hit.display_name.split(', ')[0] };
}

export function createSearchBox({ map, input, clearBtn, resultList, onPick, onKategorie }) {
  let timer;
  let controller;
  let items = [];

  const meter = (a, b) => {
    const R = 6371008.8, r = (d) => (d * Math.PI) / 180;
    const x = r(b.lng - a.lng) * Math.cos(r((a.lat + b.lat) / 2));
    return Math.hypot(x, r(b.lat - a.lat)) * R;
  };
  const kurz = (m) => (m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

  /**
   * Alle Punkte einer Kategorie im aktuellen Bild.
   *
   * Bewusst `querySourceFeatures` statt `queryRenderedFeatures`: Letzteres
   * kennt nur, was auch gezeichnet wurde - und bei dichter Beschriftung
   * verdrängt MapLibre die Hälfte. Gesucht ist aber, was *da* ist.
   */
  function imBild(classes) {
    const erlaubt = new Set(classes);
    const b = map.getBounds();
    const mitte = map.getCenter();
    const gesehen = new Set();
    const treffer = [];
    for (const f of map.querySourceFeatures('osm', { sourceLayer: 'poi' })) {
      const p = f.properties || {};
      if (!erlaubt.has(p.class)) continue;
      const name = p['name:de'] || p.name_de || p.name;
      if (!name) continue;
      const g = f.geometry;
      if (g?.type !== 'Point') continue;
      const [lng, lat] = g.coordinates;
      if (lng < b.getWest() || lng > b.getEast() || lat < b.getSouth() || lat > b.getNorth()) continue;
      const schluessel = f.id ?? `${name}@${lng.toFixed(5)},${lat.toFixed(5)}`;
      if (gesehen.has(schluessel)) continue;
      gesehen.add(schluessel);
      treffer.push({
        name, lat, lng,
        klasse: p.class,
        // OpenMapTiles führt z.B. Ladestationen unter der Klasse "fuel".
        // Für die Beschriftung ist die Unterklasse die ehrlichere Angabe.
        unterklasse: p.subclass,
        abstand: meter(mitte, { lat, lng })
      });
    }
    return treffer.sort((a, b2) => a.abstand - b2.abstand);
  }

  function renderKategorie(kat, liste) {
    resultList.textContent = '';
    const kopf = document.createElement('li');
    kopf.className = 'is-head';
    kopf.textContent = liste.length
      ? `${kat.label} im Kartenausschnitt (${liste.length})`
      : `${kat.label}: nichts im Kartenausschnitt`;
    resultList.append(kopf);

    if (!liste.length && map.getZoom() < POI_AB_ZOOM) {
      const hinweis = document.createElement('li');
      hinweis.className = 'is-empty';
      hinweis.textContent = 'Zu weit draussen - näher heranzoomen.';
      resultList.append(hinweis);
    }

    for (const t of liste.slice(0, 40)) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'option');
      btn.innerHTML = '<strong></strong><small></small>';
      btn.querySelector('strong').textContent = t.name;
      const art = t.unterklasse && t.unterklasse !== t.klasse
        ? classLabel(t.unterklasse)
        : classLabel(t.klasse);
      btn.querySelector('small').textContent = `${art} · ${kurz(t.abstand)}`;
      btn.addEventListener('click', () => {
        map.flyTo({ center: [t.lng, t.lat], zoom: Math.max(map.getZoom(), 17), duration: 700 });
        // Den Suchbegriff stehen lassen: die Einschränkung gilt weiter, und
        // beim nächsten Verschieben zählt die Liste neu durch.
        hide();
        onPick?.({ name: t.name, lat: t.lat, lng: t.lng, display: t.name });
      });
      li.append(btn);
      resultList.append(li);
    }
    resultList.hidden = false;
  }

  const hide = () => {
    resultList.hidden = true;
    resultList.textContent = '';
    items = [];
  };

  /** Von aussen aufräumen - etwa wenn der Filter die Kategorie aufhebt. */
  function leeren() {
    input.value = '';
    hide();
  }

  function render(list) {
    resultList.textContent = '';
    if (!list.length) {
      const li = document.createElement('li');
      li.className = 'is-empty';
      li.textContent = 'Nichts gefunden.';
      resultList.append(li);
      resultList.hidden = false;
      return;
    }
    list.forEach((r, i) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'option');
      btn.dataset.index = String(i);
      const [head, ...rest] = r.display_name.split(', ');
      btn.innerHTML = `<strong></strong><small></small>`;
      btn.querySelector('strong').textContent = r.name || head;
      btn.querySelector('small').textContent = rest.join(', ');
      btn.addEventListener('click', () => pick(r));
      li.append(btn);
      resultList.append(li);
    });
    resultList.hidden = false;
  }

  function pick(r) {
    const lat = Number(r.lat);
    const lng = Number(r.lon);
    if (r.boundingbox) {
      const [s, n, w, e] = r.boundingbox.map(Number);
      map.fitBounds([[w, s], [e, n]], { padding: 60, maxZoom: 17, duration: 800 });
    } else {
      map.flyTo({ center: [lng, lat], zoom: 16, duration: 800 });
    }
    input.value = r.name || r.display_name.split(', ')[0];
    hide();
    onPick?.({ name: input.value, lat, lng, display: r.display_name });
  }

  async function run(q) {
    controller?.abort();
    controller = new AbortController();

    // Erst schauen, ob eine Kategorie gemeint ist - das beantwortet die Frage
    // aus den eigenen Kacheln, ohne einen fremden Dienst zu fragen.
    const kat = kategorieFinden(q);
    if (kat) {
      // Die Karte zeigt ab jetzt nur noch diese Art - sonst sucht man den
      // einen Treffer zwischen hundert fremden Symbolen.
      onKategorie?.(kat);
      renderKategorie(kat, imBild(kat.classes));
      return;
    }
    onKategorie?.(null);
    const b = map.getBounds();
    const params = new URLSearchParams({
      q,
      format: 'jsonv2',
      limit: '8',
      addressdetails: '0',
      'accept-language': 'de-CH',
      viewbox: [b.getWest(), b.getNorth(), b.getEast(), b.getSouth()].join(','),
      bounded: '0'
    });
    try {
      const res = await fetch(`${ENDPOINT}?${params}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error(String(res.status));
      items = await res.json();
      render(items);
    } catch (err) {
      if (err.name !== 'AbortError') hide();
    }
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearBtn.hidden = !q;
    clearTimeout(timer);
    if (q.length < 3) return hide();
    timer = setTimeout(() => run(q), 400);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { hide(); input.blur(); }
    if (e.key === 'Enter' && items.length) { e.preventDefault(); pick(items[0]); }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      resultList.querySelector('button')?.focus();
    }
  });

  clearBtn.addEventListener('click', () => {
    input.value = '';
    clearBtn.hidden = true;
    hide();
    // Mit dem Suchbegriff geht auch die Einschränkung der Karte weg.
    onKategorie?.(null);
    input.focus();
  });

  document.addEventListener('click', (e) => {
    if (!resultList.hidden && !e.target.closest('.search')) hide();
  });

  return { hide, leeren };
}
